import QueryString from 'qs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import RotatePhone from '~/RotatePhone';
import { useOptions } from '~/api';
import { useAssets } from '~/components/AssetSelect';
import { AuthGate } from '~/components/AuthGate';
import { FloatingActionButton } from '~/components/FloatingActionButton';
import { ListDetailView } from '~/components/ListDetailView';
import { AssetConfigTable } from '~/components/asset-config/AssetConfigTable';
import { StockManipulationComponent } from '~/components/smc/StockManipulationComponent';
import { TransferStock } from '~/components/stock/TransferStock';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { NavContainer } from '~/components/ui/NavContainer';
import { Result } from '~/components/ui/Result';
import { DisplayState, StatefulView } from '~/components/ui/StatefulView';
import { Switch } from '~/components/ui/nucleus/Switch';
import { withModal } from '~/components/withModal';
import {
  ItemType,
  LocationType,
  Status,
  useLocationsQuery,
  useSitesQuery,
  useSiteTypesQuery,
  useSpacesQuery,
} from '~/generated/graphql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useDynamicHeaderMargin } from '~/hooks/useDynamicHeaderMargin';
import { useEnumOptions } from '~/hooks/useEnumOptions';
import { AssetsNav } from '~/layouts/nav/AssetsNav';
import { SitesNav } from '~/layouts/nav/SitesNav';
import { StockNav } from '~/layouts/nav/StockNav';
import { AddItems } from './AddItems';
import { AddSpaces } from './AddSpaces';
import { AdjustStock } from './AdjustStock';
import { AssetConfigActions } from './AssetConfigActions';
import { CloneSpaces } from './CloneSpaces';
import { CommitChanges } from './CommitChanges';
import { ModifyProperties } from './ModifyProperties';
import { RemoveItems } from './RemoveItems';
import { UnqueueChanges } from './UnqueueChanges';

const ModifyPropertiesModal = withModal(ModifyProperties);
const RemoveItemsModal = withModal(RemoveItems);
const AddItemsModal = withModal(AddItems);
const UnqueueChangesModal = withModal(UnqueueChanges);
const CommitChangesModal = withModal(CommitChanges);
const AddSpacesModal = withModal(AddSpaces);
const CloneSpacesModal = withModal(CloneSpaces);
const AdjustStockModal = withModal(AdjustStock);
const SMCModal = withModal(StockManipulationComponent);
const TransferStockModal = withModal(TransferStock);

export type ConfigProps = {
  locationType?: 'Asset' | 'Site';
};

export const AssetConfig = ({ locationType }: ConfigProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { filterRowRef, dynamicMarginTop } = useDynamicHeaderMargin();
  const [selected, setSelected] = useState<Selected>({});
  const [onlySelected, setOnlySelected] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');
  const [filtersValue, setFiltersValue] = useState<URLSearchParams>(() => {
    const [, queryString] = window.location.href.split('?', 2);
    const locationId = queryString && QueryString.parse(queryString).locationId;
    const initialParams = new URLSearchParams();
    if (typeof locationId === 'string') {
      initialParams.set('locationIds', locationId);
    }
    initialParams.set('locationStatus', Status.Active);
    return initialParams;
  });

  const { data: skuOptionsData } = useOptions('skus');
  const itemTypeOptions = useEnumOptions(ItemType, 'itemType');
  const [{ data: locationsData }] = useLocationsQuery({
    variables: { type: LocationType.Site },
  });
  const [{ data: sitesData }] = useSitesQuery();
  const [{ data: siteTypesData }] = useSiteTypesQuery();
  const [{ data: spacesData }] = useSpacesQuery();
  const statusOptions = Object.values(Status).map((value) => ({
    value,
    label: t(`statuses.${value}`),
  }));

  // Create specific options for the locationStatus filter pill display
  const locationStatusFilterOptions = useMemo(
    () =>
      Object.values(Status).map((value) => ({
        value,
        label: `${t(`statuses.${value}`)} ${t('location_plural')}`,
      })),
    [t]
  );

  const [assetsData, ,] = useAssets();

  const locationIdOptions = useMemo(
    () =>
      locationsData?.locations
        ?.filter((loc) => loc && loc.id && loc.name && loc.__typename)
        .map(({ __typename: type, id, name }) => ({
          value: `${type}:${id}`,
          label: name,
        })) ?? [],
    [locationsData]
  );
  const assetOptions = useMemo(() => {
    return (
      assetsData
        ?.filter((asset) => asset && asset.id && asset.name)
        .map(({ id, name }) => ({
          value: id,
          label: name,
        })) ?? []
    );
  }, [assetsData]);
  const assetModelOptions = useMemo(() => {
    if (!assetsData) return [];
    const modelsMap = new Map<string, string>();
    assetsData.forEach((asset) => {
      if (asset.model?.id && asset.model?.name) {
        modelsMap.set(asset.model.id, asset.model.name);
      }
    });
    return Array.from(modelsMap.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    }));
  }, [assetsData]);
  const siteNameOptions = useMemo(
    () =>
      sitesData?.sites
        ?.filter((site) => site && site.name)
        .map(({ name: value }) => ({ value, label: value })) ?? [],
    [sitesData]
  );
  const siteTypeOptions = useMemo(
    () =>
      siteTypesData?.siteTypes
        ?.filter((value) => typeof value === 'string' && !!value)
        .map((value) => ({ value, label: value }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [siteTypesData]
  );
  const spaceNameOptions = useMemo(
    () =>
      spacesData?.spaceNames
        ?.filter((value) => typeof value === 'string' && !!value)
        .map((value) => ({ value, label: value })) ?? [],
    [spacesData]
  );
  const skuSelectOptions = useMemo(
    () => skuOptionsData ?? [],
    [skuOptionsData]
  );

  const query = useMemo(() => {
    const filterObjectForQs: Record<string, any> = {};

    // Convert URLSearchParams from filtersValue to a plain object for qs
    for (const [key, value] of filtersValue.entries()) {
      const existing = filterObjectForQs[key];
      if (existing) {
        if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          filterObjectForQs[key] = [existing, value];
        }
      } else {
        filterObjectForQs[key] = value;
      }
    }

    // searchValue (q) is NOT added here; it will be handled client-side

    if (Object.keys(filterObjectForQs).length > 0) {
      return QueryString.stringify(
        { filters: filterObjectForQs },
        { arrayFormat: 'comma', encodeValuesOnly: true }
      );
    }
    return '';
  }, [filtersValue]); // Remove searchValue from dependencies, query only depends on dropdowns

  const {
    data,
    error,
    isValidating,
    mutate: revalidate,
  } = useSWR<AssetConfigData[]>(
    `/api/asset-config?${
      locationType ? `locationTypes=${locationType}&` : ''
    }${query}`,
    {
      revalidateOnFocus: false,
    }
  );

  const isSWRLoading = (data === undefined && !error) || isValidating;

  const initialLoadComplete = useRef(false);
  const [isTableRendering, setIsTableRendering] = useState(false);
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    if (!isSWRLoading && data !== undefined && !initialLoadComplete.current) {
      initialLoadComplete.current = true;
      setIsTableRendering(true);
      renderTimeoutRef.current = setTimeout(() => {
        setIsTableRendering(false);
      }, 300);
    } else if (isSWRLoading) {
      setIsTableRendering(false);
      if (data === undefined) {
        initialLoadComplete.current = false;
      }
    }

    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [isSWRLoading, data]);

  useEffect(() => {
    if (Object.keys(selected).length === 0) {
      setOnlySelected(false);
    }
  }, [selected]);

  const view = useMemo(() => {
    switch (locationType) {
      case 'Asset':
        return {
          path: '/asset-config',
          Nav: AssetsNav,
        };
      case 'Site':
        return {
          path: '/site-config',
          Nav: SitesNav,
        };
      default:
        return {
          path: '/config',
          Nav: StockNav,
        };
    }
  }, [locationType]);

  const back = useCallback(() => navigate(view.path), [navigate, view.path]);

  const { isMobile } = useBreakpoint();

  const assetConfig = useMemo(() => {
    let filteredData = data ?? [];

    // Apply searchValue filter client-side
    if (searchValue && searchValue.trim() !== '') {
      const lowerSearchValue = searchValue.toLowerCase().trim();
      filteredData = filteredData.filter((item) => {
        const assetName = item.asset_name?.toLowerCase() || '';
        const spaceName = item.space_name?.toLowerCase() || '';
        const skuCode = item.sku_code?.toLowerCase() || '';
        const skuName = item.sku_name?.toLowerCase() || '';
        const locationName = item.location_name?.toLowerCase() || '';

        return (
          assetName.includes(lowerSearchValue) ||
          spaceName.includes(lowerSearchValue) ||
          skuCode.includes(lowerSearchValue) ||
          skuName.includes(lowerSearchValue) ||
          locationName.includes(lowerSearchValue)
        );
      });
    }

    if (onlySelected) {
      filteredData = filteredData.filter(({ id }) =>
        Object.keys(selected).includes(id)
      );
    }
    return filteredData;
  }, [data, searchValue, onlySelected, selected]); // Add searchValue back as a dependency

  const hasSelected = Object.keys(selected).length > 0;

  const handleSuccess = () => {
    revalidate();
    back();
  };

  const childProps = {
    selected,
    data: assetConfig,
    onSuccess: handleSuccess,
  };

  const handleFilterChange = useCallback(
    ({
      searchValue: newSearchValue,
      filtersValue: newFiltersValue,
    }: {
      searchValue: string;
      filtersValue: URLSearchParams;
    }) => {
      setSearchValue(newSearchValue);
      setFiltersValue(newFiltersValue);
    },
    []
  );

  const areFiltersActive = useMemo(() => filtersValue.size > 0, [filtersValue]);
  const displayState = useMemo((): DisplayState => {
    if (isSWRLoading && data === undefined) return 'INITIAL_LOADING';
    if (isSWRLoading) return 'LOADING';
    if (error) return 'ERROR';
    if (assetConfig.length === 0) return 'NO_RESULTS';
    return 'IDLE';
  }, [isSWRLoading, data, error, assetConfig.length]);
  const isSearchResultEmpty = useMemo(() => {
    return (
      displayState === 'NO_RESULTS' &&
      !!searchValue &&
      !!data &&
      data.length > 0
    );
  }, [displayState, searchValue, data]);

  const filterConfig = useMemo(() => {
    const baseFilters = [
      {
        name: 'spaceNames',
        label: t('spaceName'),
        options: spaceNameOptions,
        type: 'select' as const,
        multiple: true,
      },
      {
        name: 'itemType',
        label: t('itemTypeLabel'),
        options: itemTypeOptions,
        type: 'select' as const,
        multiple: true,
      },
      {
        name: 'skus',
        label: t('itemName'),
        options: skuSelectOptions,
        type: 'select' as const,
        multiple: true,
      },
    ];

    if (locationType === 'Asset') {
      return [
        ...baseFilters,
        {
          name: 'assets',
          label: t('assetName'),
          options: assetOptions,
          type: 'select' as const,
          multiple: true,
        },
        {
          name: 'assetModels',
          label: t('assetModel'),
          options: assetModelOptions,
          type: 'select' as const,
          multiple: true,
        },
      ];
    } else if (locationType === 'Site') {
      return [
        ...baseFilters,
        {
          name: 'siteNames',
          label: t('siteName'),
          options: siteNameOptions,
          type: 'select' as const,
          multiple: true,
        },
        {
          name: 'siteTypes',
          label: t('siteType'),
          options: siteTypeOptions,
          type: 'select' as const,
          multiple: true,
        },
      ];
    } else {
      return [
        ...baseFilters,
        {
          name: 'locationIds',
          label: t('location'),
          options: locationIdOptions,
          type: 'select' as const,
          multiple: true,
        },
        {
          name: 'locationStatus',
          label: t('locationStatus'),
          options: locationStatusFilterOptions,
          type: 'select' as const,
          multiple: true,
          selectAll: false,
          searchable: false,
        },
      ];
    }
  }, [
    t,
    locationType,
    spaceNameOptions,
    itemTypeOptions,
    skuSelectOptions,
    assetOptions,
    assetModelOptions,
    siteNameOptions,
    siteTypeOptions,
    locationIdOptions,
    locationStatusFilterOptions,
  ]);

  return (
    <AuthGate action='manage' subject='feat.items'>
      <NavContainer nav={<view.Nav />}>
        <NavContainer.FilterRow ref={filterRowRef}>
          <FilterGroupSearch
            filters={filterConfig}
            value={{ searchValue, filtersValue }}
            onChange={handleFilterChange}
            placeholder={`Find ${
              locationType ? locationType + ' ' : ''
            }Configurations...`}
          />
          {hasSelected && (
            <Switch
              type='filter'
              checked={onlySelected}
              onChange={() => setOnlySelected((state) => !state)}
              label='Only show selected'
            />
          )}
          <div className='ml-auto hidden whitespace-nowrap px-4 text-right lg:block'>
            <a
              className='text-brand hover:underline'
              href={`${
                process.env.NODE_ENV === 'development'
                  ? 'http://localhost:4000'
                  : ''
              }/api/asset-config?format=csv&${query}`}
            >
              {t('export')}
            </a>
          </div>
        </NavContainer.FilterRow>
      </NavContainer>
      <ListDetailView
        disableMainScrollContainer={true}
        nav={<view.Nav />}
        dynamicMarginTop={dynamicMarginTop}
        main={
          <>
            <div className='mb-20 flex h-full flex-col justify-center bg-white fill-copy-body px-16 md:hidden'>
              <RotatePhone />
              <p className='text-center'>Rotate to view</p>
            </div>
            <div className='hidden h-full flex-grow flex-col gap-3 pt-4 md:flex'>
              <Result count={data?.length} />
              <StatefulView
                className='h-full'
                displayState={displayState}
                activeFilters={areFiltersActive}
                isSearchResultEmpty={isSearchResultEmpty}
                entityName={
                  locationType === 'Site'
                    ? 'site configurations'
                    : locationType === 'Asset'
                    ? 'asset configurations'
                    : 'configurations'
                }
              >
                <AssetConfigTable
                  locationType={locationType}
                  data={assetConfig}
                  selected={selected}
                  setSelected={setSelected}
                  isLoading={isTableRendering}
                />
              </StatefulView>
            </div>
          </>
        }
        actions={<AssetConfigActions selected={selected} />}
      >
        <Route path='*' element={<ConfigFAB />} />
        <Route
          path='modify-properties'
          element={<ModifyPropertiesModal {...childProps} />}
        />
        <Route
          path='remove-items'
          element={<RemoveItemsModal {...childProps} />}
        />
        <Route
          path='add-items'
          element={<AddItemsModal {...childProps} back={back} />}
        />
        <Route
          path='unqueue-changes'
          element={<UnqueueChangesModal {...childProps} />}
        />
        <Route
          path='commit-changes'
          element={<CommitChangesModal {...childProps} />}
        />
        <Route
          path='add-spaces'
          element={<AddSpacesModal onClose={back} onSuccess={handleSuccess} />}
        />

        <Route
          path='clone-spaces'
          element={<CloneSpacesModal {...childProps} onClose={back} />}
        />

        <Route
          path='stock-adjustment'
          element={<AdjustStockModal {...childProps} onClose={back} />}
        />

        <Route
          path='stocktake'
          element={
            <SMCModal
              mode='stocktake'
              onSuccess={childProps.onSuccess}
              onCancel={() => navigate('/config')}
            />
          }
        />

        <Route
          path='transfer-stock'
          element={<TransferStockModal returnTo={view.path} />}
        />
      </ListDetailView>
    </AuthGate>
  );
};

function ConfigFAB() {
  const { t } = useTranslation();
  return (
    <FloatingActionButton
      actions={[
        {
          to: 'add-items',
          label: t('addItems'),
        },
        {
          to: 'remove-items',
          label: t('removeItems'),
        },
        {
          to: 'stock-adjustment',
          label: t('adjustStock'),
        },
        {
          to: 'stocktake',
          label: t('stocktake'),
        },
        {
          to: 'modify-properties',
          label: t('modifyProperties'),
        },
      ]}
    />
  );
}

export type AssetConfigData = {
  id: string;
  status: 'active' | 'queued';
  queued_type: 'Add' | 'Remove' | null;
  asset_id: number;
  asset_name: string;
  space_sku_id: number;
  space_id: number;
  space_name: string;
  sku_id: number;
  sku_code: string;
  sku_name: string;
  item_type: string;
  price: number;
  soh: number;
  reserved: number;
  capacity: number;
  empty: number;
  target: number;
  missing: number;
  available: number;
  queued_price: number | null;
  queued_capacity: number | null;
  queued_target: number | null;
  location_name: string;
  image: string | null;
  reorder_level: string | number | null;
};

export type Selected = Record<string, boolean>;
